import { getSession, signIn } from 'next-auth/react'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

function extractErrorMessage(errorData: unknown, fallback: string): string {
  if (errorData && typeof errorData === 'object') {
    const data = errorData as {
      error?: { message?: string }
      detail?: unknown
    }
    if (data.error?.message) return data.error.message
    if (typeof data.detail === 'string') return data.detail
  }
  return fallback
}

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const session = await getSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    // Force sign-in to get a new, valid session.
    await signIn('keycloak')
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    throw new Error(extractErrorMessage(errorData, 'An API error occurred.'))
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },

  /**
   * Perform a multipart upload. The browser sets the boundary header.
   */
  upload: <T = unknown>(
    endpoint: string,
    formData: FormData,
    options?: RequestInit
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
    })
  },

  /**
   * Fetch a protected binary resource (e.g. an invoice document).
   */
  getBlob: (endpoint: string, options?: RequestInit): Promise<Blob> => {
    return apiClientBlob(endpoint, options)
  },
}

async function apiClientBlob(
  endpoint: string,
  options: RequestInit = {}
): Promise<Blob> {
  const session = await getSession()
  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }
  if (session?.error === 'RefreshAccessTokenError') {
    await signIn('keycloak')
    throw new Error('Session expired. Please sign in again.')
  }

  const response = await fetch(`${API_URL}${BASE_PATH}${endpoint}`, {
    ...options,
    headers,
  })

  if (response.status === 401) {
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(extractErrorMessage(errorData, 'Could not load the document.'))
  }
  return response.blob()
}

// Default export for backward compatibility
export default apiClientFetch
